import { node } from 'prop-types'
import type { CSSProperties } from 'react'
import { Header } from 'semantic-ui-react'
import ImgTemplate from '../../../../utilites/ImgTemplate'

const paragraphStyle = {
  marginBottom: '1em',
  textAlign: 'justify',
} satisfies CSSProperties

function SpaceWeather() {
  return (
    <div className='fade-in'>
      <Header as='h1' style={{ textAlign: 'center' }}>
        Space weather
      </Header>

      <h3>What is Space weather?</h3>
      <div style={paragraphStyle}>
        Space weather (SW) is the current state of the space plasma and fields, and the ensemble of physical processes
        that cause dynamic changes in the plasma and magnetic fields throughout the interplanetary space of the Solar
        System — particularly between the Sun and the Earth. These variations are mainly driven by the Sun through the
        emission of electromagnetic radiation, ejections of the streams of energetic particles, and ejections of plasma
        and magnetic fields.
      </div>
      <div style={paragraphStyle}>The principal solar phenomena defining space weather include:</div>
      <ul style={paragraphStyle}>
        <li>
          Emissions across the full electromagnetic spectrum (gamma rays, hard and soft X-rays, extreme
          ultraviolet/ultraviolet, visible light, infrared, and radio waves),
        </li>
        <li>Streams of high-energy charged particles known as Solar Energetic Particles (SEP),</li>
        <li>Eruptions and propagation of Coronal Mass Ejections (CME),</li>
        <li>Variations in solar wind (SW) parameters, including high-speed solar wind streams (HSS),</li>
        <li>Changes in the Interplanetary Magnetic Field (IMF).</li>
      </ul>
      <div style={paragraphStyle}>
        Research in space weather aims to monitor, analyze, and model these processes using both ground-based and
        in-situ observations as well as extended physical and mathematical methods and tools. Its objectives are to
        understand the mechanisms shaping space weather, forecast solar and interplanetary conditions, predict
        disturbances caused directly or indirectly by solar activity, and evaluate their impacts on planetary
        environments. Additionally, SW studies seek to assess potential effects on biological and technological systems
        on Earth and in space.
      </div>

      {/* Figure 1 */}
      <div style={{ textAlign: 'center' }}>
        <ImgTemplate
          srcName='spaceweather/figure1.webp'
          alt='The future ESA Vigil mission will provide a first-of-its-kind capability: monitoring the Sun to
          provide constant, near real-time data on potentially hazardous solar activity (credit: ESA/A. Baker)'
          width='100%'
        />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Figure 1. The future ESA Vigil mission will provide a first-of-its-kind capability: monitoring the Sun to
          provide constant, near real-time data on potentially hazardous solar activity (credit: ESA/A. Baker).
        </p>
      </div>

      <h3>Solar Drivers and Interplanetary Propagation</h3>
      <div style={paragraphStyle}>
        The main drivers of space weather are processes linked to solar magnetic activity and the continuous outflow of
        plasma and fields from the Sun. The solar magnetic field extends throughout the Solar System as the
        Interplanetary Magnetic Field (IMF), and the solar plasma fills the heliosphere up to the heliopause. Beyond
        solar factors, other influences such as Galactic Cosmic Rays (GCR) also shape space weather by modifying
        near-Earth conditions.
      </div>
      <div style={paragraphStyle}>
        The Sun produces space weather phenomena (like solar flares, coronal mass ejections (CMEs), solar energetic
        particle (SEP) beams) with variable intensity and frequency, modulated by solar magnetic activity cycles. The
        fundamental cycle is the 11-year Schwabe cycle, easily monitored via sunspot numbers (Wolf or ISN indices).
        Longer-term cycles, such as the ≈88-year, ≈203-year, and possibly ≈2300-year cycles, are superimposed on the
        basic pattern. On shorter timescales, the influence of solar phenomena is modulated by the Carrington synodic
        rotation period (~27.3 days). During solar maximum, the frequency of solar flares, CMEs, SEP events, and IMF
        intensity peaks, whereas the flux of GCR is at its minimum, anti-correlated with the IMF.
      </div>
      <div style={{ textAlign: 'center' }}>
        <ImgTemplate
          srcName='spaceweather/figure2.webp'
          alt='Parameters of the interplanetary plasma (density, velocity) (credit: SPWC NOAA;
          https://www.swpc.noaa.gov/products/wsa-enlil-solar-wind-prediction)'
          width='100%'
        />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Figure 2: Parameters of the interplanetary plasma (density, velocity) (credit: SPWC NOAA;
          https://www.swpc.noaa.gov/products/wsa-enlil-solar-wind-prediction)
        </p>
      </div>

      <h3>Solar Flares</h3>
      <div style={paragraphStyle}>
        A solar flare is a complex event triggered by the sudden release of enormous energy (10²⁴–10²⁶ joules) in the
        solar corona, mostly within the first few minutes. This energy is stored in non-potential magnetic fields prior
        to the flare. The maximum phase, or peak emission period, usually lasts several tens of minutes, corresponding
        to a power output of the flare of roughly 3–5 × 10²¹ W — about one hundred-thousandth of the Sun’s total
        radiative power only. However, the impact of the various forms of radiation and energy released during a solar
        flare is disproportionately greater than what would be expected from its relative power alone. For example,
        roughly one-third of this energy is emitted as X-rays and ultraviolet radiation, producing fluxes up to a
        thousand times stronger than the entire quiet solar corona. Such energy can easily heat and little expand
        Earth’s ionosphere or have a detrimental effect on the health of astronauts.
      </div>
      <div style={{ textAlign: 'center' }}>
        <ImgTemplate
          srcName='spaceweather/figure3.webp'
          alt='Strong  X3-class solar flare captured on October 24, 2014 in the active region NOAA 12192 y(credit: Solar Dynamic Observatory/AIA, NASA; Miroslav Druckmuller)'
          width='100%'
        />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', marginBottom: '0.4em', fontStyle: 'italic' }}>
          Figure 3: Strong X3-class solar flare captured on October 24, 2014 in the active region NOAA 12192 y(credit:
          Solar Dynamic Observatory/AIA, NASA; Miroslav Druckmuller)
        </p>
      </div>
      <div style={paragraphStyle}>
        Flares heat local coronal plasma to several million kelvin and are often accompanied by CMEs, SEP streams,
        eruptive prominences (EP), shock waves, and emissions across the full electromagnetic spectrum. In solar cycle
        23 (C23), for example, approximately 10,000 C-class flares (minor influence), 1,000 M-class flares (moderate
        influence), and 100 X-class flares (extreme events) were recorded (the broadly understood magnitude of solar
        flares is generally classified using the so-called GOES classes, ranging from A, through B, C and M, up to X).
      </div>

      <h3>Coronal Mass Ejections (CME)</h3>
      <div style={paragraphStyle}>
        CMEs are massive, magnetized plasma structures propagating through interplanetary space. They are
        extraordinarily powerful: average speeds are ~500 km/s (ranging from 20 km/s to &gt; 3,500 km/s), with masses
        around 1.6 × 10¹² kg and kinetic energies of 10²²–10²⁵ J. Over 13,000 CMEs were observed during solar cycle 23,
        with up to six CMEs ejected per day during maximum activity.
      </div>
      {/* Figure 4 */}
      <div style={{ textAlign: 'center' }}>
        <ImgTemplate
          srcName='spaceweather/fiigure4.webp'
          alt='A snapshot of the multi-viewpoint GCS analysis for one of the CMEs expanding through the space (credit: ESA, https://swe.ssa.esa.int/use-of-l5-data-in-cme-propagation-models).'
          width='100%'
        />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Figure 4. A snapshot of the multi-viewpoint GCS analysis for one of the CMEs expanding through the space
          (credit: ESA, https://swe.ssa.esa.int/use-of-l5-data-in-cme-propagation-models).
        </p>
      </div>

      <h3>Solar Energetic Particles (SEP)</h3>
      <div style={paragraphStyle}>
        SEP events, associated with some CMEs (~1%), contain electrons, protons, and ions with energies from keV to GeV.
        Fluxes may exceed 10⁸–10¹⁰ particles cm⁻² s⁻¹ sr⁻¹. SEP events can last from minutes (impulsive) to several days
        (gradual). High-energy SEP can penetrate spacecraft outside the magnetosphere, disrupting electronics or
        endangering unshielded astronauts, as observed in events of August 1972 and October–November 2003.
      </div>

      <h3>Impacts on Earth’s Magnetosphere and Atmosphere</h3>
      <div style={paragraphStyle}>
        All SW phenomena alter plasma conditions, magnetic fields, and radiation near Earth, affecting interactions with
        the magnetosphere and atmosphere. Earth’s magnetic field provides partial protection, but under strong solar
        events, energy and momentum can enter the geospace system, triggering global effects. Typical manifestations are
        geomagnetic storms, which induce electric currents, heat the ionosphere, and produce aurorae.
      </div>
      {/* Figure 5 */}
      <div style={{ textAlign: 'center' }}>
        <ImgTemplate
          srcName='spaceweather/figure5.webp'
          alt='Impact of space weather on various areas of technological activity  (credit: ESA, https://swe.ssa.esa.int/protect-space-weather-demonstrator)..'
          width='100%'
        />
        <p style={{ fontSize: '0.9em', color: '#555', marginTop: '0.4em', fontStyle: 'italic' }}>
          Figure 5: Impact of space weather on various areas of technological activity (credit: ESA,
          https://swe.ssa.esa.int/protect-space-weather-demonstrator).
        </p>
      </div>

      <h3>Technological and Societal Impacts</h3>
      <div style={paragraphStyle}>Space weather affects multiple sectors:</div>
      <ul style={paragraphStyle}>
        <li>
          Telecommunications: satellite and ground‑based, radio transmissions, meteorological services, reconnaissance.
        </li>
        <li>Navigation: GNSS systems (GPS, Galileo).</li>
        <li>Power systems: high‑voltage networks, transformers.</li>
        <li>
          Spacecraft: orbital decay, discharges, electronics disruptions, radiation exposure, material degradation.
        </li>
      </ul>
      <div style={paragraphStyle}>Historical examples:</div>
      <ul style={paragraphStyle}>
        <li>13 March 1989: 9-hour blackout in Canada, transformer failures, satellite orbit drop of ~5 km.</li>
        <li>1994: radio/TV outages, two satellites damaged.</li>
        <li>October–November 2003: widespread radio communication failures.</li>
      </ul>
      <div style={paragraphStyle}>
        Numerical models indicate that extreme geomagnetic storms (e.g., 4800 nT/min gradient like May 1921) could
        severely damage power distribution across large regions.
      </div>

      <h3>Space weather Forecasting and Observations</h3>
      <div style={paragraphStyle}>
        Monitoring SW requires multi-spectral, remote-sensing, and in-situ observations of the Sun, heliosphere,
        magnetosphere, and ionosphere. Tools include:
      </div>
      <ul style={paragraphStyle}>
        <li>Satellites: SOHO, Parker Solar Probe, DSCOVR, GOES, AIA/SDO, HOLO, XRISM, etc.</li>
        <li>
          Ground-based networks: optical telescopes, radio telescopes, cosmic ray detectors, ionosondes, and
          magnetometers.
        </li>
      </ul>
      <div style={paragraphStyle}>
        Data support both real-time monitoring and numerical modeling, enabling forecasting of solar activity,
        interplanetary changes, and geophysical impacts. This forms the basis for early warning systems to protect
        orbital and ground infrastructure. The comprehensive understanding of space weather will allows scientists to
        monitor, forecast, and mitigate the effects of solar activity on Earth and in space, supporting both scientific
        research and the protection of critical infrastructure.
      </div>

      <h3>Space weather Indices</h3>
      <div style={paragraphStyle}>Key indices used to describe geomagnetic conditions include:</div>
      <ul style={paragraphStyle}>
        <li>
          Kp index: 3-hourly global geomagnetic disturbance scale (0–9). Kp ≥ 5 indicates a geomagnetic storm and
          potential aurora at mid-latitudes.
        </li>
        <li>
          Dst index: global depression of Earth’s magnetic field (mainly dipole); negative values indicate significant
          reduction.
        </li>
        <li>
          Ap index: average 3-hour geomagnetic disturbance based on global magnetometer network; expressed in nanotesla
          (0–400). Higher values indicate stronger geomagnetic perturbations.
        </li>
      </ul>
      <div style={paragraphStyle}>
        More information about space weather and current monitoring is available on ESA’s Space weather Service Network:{' '}
        <a href='https://swe.ssa.esa.int/' target='_blank' rel='noreferrer'>
          https://swe.ssa.esa.int/
        </a>
        .
      </div>
    </div>
  )
}

SpaceWeather.propTypes = {
  children: node,
}

export default SpaceWeather
